/* eslint-disable react/no-danger */

import { MortgageAnnotations } from '@cian/adfox-templates';
import { Label } from '@cian/ui-kit';

import cn from 'classnames';
import React, { useCallback, useMemo } from 'react';
import { sanitize } from 'isomorphic-dompurify';

import { IHousingInfo } from '../../types/housing_info';
import { declineNumeric, formatPrice } from '../../utils/format_helper';
import { Link } from '../link/link';
import { FILTERS_BLOCK_HEIGHT } from '../adfox/adfoxNew';

import styles from './styles.css';

export interface IMediaBannerProps {
  hasNewPlacement: boolean;
  url?: string;
  logoImageUrl?: string;
  logoWithoutBackground?: string;
  substrateNotNeeded?: string;
  name?: string;
  actionText?: string;
  phone?: string;
  buttonText?: string;
  info?: string;
  label?: string;
  infoText?: string;
  isApartment?: string;
  housingInfo?: IHousingInfo;
  mortgageWarning: string | undefined;
  mortgageTermsWarning: string | undefined;
}

export const MediaBanner: React.FC<IMediaBannerProps> = ({
  hasNewPlacement,
  url = '',
  logoImageUrl = '',
  logoWithoutBackground = '0',
  substrateNotNeeded = '0',
  name = '',
  actionText = '',
  phone = '',
  buttonText = 'Узнать цены',
  info = '',
  infoText,
  isApartment,
  housingInfo,
  label,
  mortgageWarning,
  mortgageTermsWarning,
}) => {
  const withMortgageAnnotations = !!(mortgageWarning || mortgageTermsWarning);

  const getInfoText = useCallback(() => {
    if (housingInfo && housingInfo.offers_count) {
      return `В продаже ${formatPrice(housingInfo.offers_count)} ${declineNumeric(
        housingInfo.offers_count,
        isApartment === '1' ? ['апартамент', 'апартамента', 'апартаментов'] : ['квартира', 'квартиры', 'квартир'],
      )} по цене от ${formatPrice(housingInfo.min_price2)} ₽`;
    }

    return undefined;
  }, [housingInfo, isApartment]);

  const sanitizedName = useMemo(() => name && sanitize(name), [name]);
  const sanitizedPhone = useMemo(() => phone && sanitize(phone), [phone]);
  const sanitizedActionText = useMemo(() => actionText && sanitize(actionText), [actionText]);
  const sanitizedInfo = useMemo(() => sanitize(infoText || getInfoText() || info), [getInfoText, info, infoText]);

  if (!logoImageUrl) {
    return <div />;
  }

  return (
    <div className={cn(styles['c-filters-brand'], hasNewPlacement && styles['c-filters-brand--new-placement'])}>
      <div className={styles['c-filters-brand-info']}>
        <a
          className={styles['c-filters-brand-flag']}
          href={url}
          target="_blank"
          role="img"
          rel="noopener noreferrer"
          dangerouslySetInnerHTML={{ __html: sanitizedActionText }}
        />
        <a
          className={
            logoWithoutBackground === '1' || substrateNotNeeded === '1'
              ? styles['c-filters-brand-logo']
              : styles['c-filters-brand-logo-bg']
          }
          href={url}
          rel="noopener noreferrer"
          role="img"
          target="_blank"
        >
          <img src={logoImageUrl} alt="logo" />
        </a>
        <div className={styles['c-filters-brand-left']}>
          <a
            className={styles['c-filters-brand-link']}
            href={url}
            rel="noopener noreferrer"
            role="button"
            target="_blank"
          >
            <h3 className={styles['c-filters-brand-title']} dangerouslySetInnerHTML={{ __html: sanitizedName }} />
            <p className={styles['c-filters-brand-text']} dangerouslySetInnerHTML={{ __html: sanitizedInfo }} />
          </a>
          <div className={styles['c-filters-brand-prices']}>
            <Link href={url} className={styles['c-filters-brand-button']} rel="noopener noreferrer" target="_blank">
              {buttonText}
            </Link>
            <span className={styles['c-filters-brand-tel']} dangerouslySetInnerHTML={{ __html: sanitizedPhone }} />
          </div>
        </div>
      </div>
      {withMortgageAnnotations && (
        <div className={styles['mortgage-disclaimer']}>
          <MortgageAnnotations
            height={FILTERS_BLOCK_HEIGHT}
            mortgageWarning={mortgageWarning}
            mortgageTermsWarning={mortgageTermsWarning}
          />
        </div>
      )}
      {!!label && (
        <div className={styles['label']}>
          <Label>{label}</Label>
        </div>
      )}
    </div>
  );
};
